import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useNavigate } from 'react-router-dom'
import { Check, X, ArrowLeft } from 'lucide-react'

const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.backgroundColor};
  padding: 40px 20px;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    color: ${props => props.theme.textPrimary};
  }
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const Subtitle = styled.p`
  font-size: 16px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 40px;
`

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${props => props.$active ? props.theme.textPrimary : props.theme.textSecondary};
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: all 0.2s;
`

const ToggleSwitch = styled.button`
  position: relative;
  width: 56px;
  height: 28px;
  background-color: ${props => props.$isAnnual ? '#667eea' : props.theme.border};
  border-radius: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isAnnual ? '30px' : '2px'};
    width: 24px;
    height: 24px;
    background-color: white;
    border-radius: 50%;
    transition: all 0.3s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`

const SaveBadge = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
`

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`

const PlanCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border: 2px solid ${props => props.theme.border};
  border-radius: 16px;
  padding: 32px;
  position: relative;
  transition: all 0.3s;

  &:hover {
    transform: scale(1.02);
    border-color: #667eea;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
  }

  @media (max-width: 968px) {
    &:hover {
      transform: scale(1.02);
    }
  }
`


const PlanName = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 8px 0;
`

const PlanDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 24px 0;
`

const PriceContainer = styled.div`
  margin-bottom: 24px;
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`

const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`

const Currency = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
`

const Amount = styled.span`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const Period = styled.span`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
`

const OriginalPrice = styled.span`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  text-decoration: line-through;
  align-self: baseline;
`

const SubscribeButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  color: ${props => props.$included ? props.theme.textPrimary : props.theme.textSecondary};

  svg {
    flex-shrink: 0;
  }
`

const FeatureIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$included ? 'rgba(102, 126, 234, 0.1)' : 'transparent'};
  color: ${props => props.$included ? '#667eea' : props.theme.textSecondary};
`

const CompareSection = styled.div`
  max-width: 1200px;
  margin: 60px auto 0;
`

const CompareTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  text-align: center;
  margin-bottom: 32px;
`

const CompareTable = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 16px;
  overflow: hidden;
`

const CompareRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  border-bottom: 1px solid ${props => props.theme.border};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
  }
`

const CompareHeader = styled.div`
  padding: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  background-color: ${props => props.theme.hoverBackground};
  text-align: ${props => props.$center ? 'center' : 'left'};
  font-size: 14px;

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`

const CompareCell = styled.div`
  padding: 16px;
  color: ${props => props.theme.textSecondary};
  text-align: ${props => props.$center ? 'center' : 'left'};
  display: flex;
  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  font-size: 14px;

  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`

export default function PricingPage() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [isAnnual, setIsAnnual] = useState(true)

  const plans = [
    {
      name: 'Free',
      description: '개인 사용자를 위한 무료 플랜',
      monthlyPrice: 0,
      annualPrice: 0,
      popular: false,
      features: [
        { text: '월 10개 퀴즈 생성', included: true },
        { text: '기본 AR 오브젝트 5개', included: true },
        { text: '최대 10문제/퀴즈', included: true },
        { text: '커뮤니티 접근', included: true },
        { text: '기본 분석 리포트', included: true },
        { text: 'Excel 업로드', included: false },
        { text: '프리미엄 AR 오브젝트', included: false },
        { text: '팀 협업 기능', included: false },
        { text: '우선 지원', included: false },
      ]
    },
    {
      name: 'Light',
      description: '더 많은 기능이 필요한 사용자',
      monthlyPrice: 9900,
      annualPrice: 7900,
      popular: false,
      features: [
        { text: '월 50개 퀴즈 생성', included: true },
        { text: 'AR 오브젝트 20개', included: true },
        { text: '최대 30문제/퀴즈', included: true },
        { text: '커뮤니티 접근', included: true },
        { text: '상세 분석 리포트', included: true },
        { text: 'Excel 업로드', included: true },
        { text: '프리미엄 AR 오브젝트', included: false },
        { text: '팀 협업 기능', included: false },
        { text: '우선 지원', included: false },
      ]
    },
    {
      name: 'Standard',
      description: '전문가 및 교육 기관용',
      monthlyPrice: 29900,
      annualPrice: 24900,
      popular: false,
      features: [
        { text: '무제한 퀴즈 생성', included: true },
        { text: '모든 AR 오브젝트', included: true },
        { text: '무제한 문제/퀴즈', included: true },
        { text: '커뮤니티 접근', included: true },
        { text: '고급 분석 리포트', included: true },
        { text: 'Excel 업로드', included: true },
        { text: '프리미엄 AR 오브젝트', included: true },
        { text: '팀 협업 기능', included: true },
        { text: '우선 지원', included: true },
      ]
    }
  ]

  const compareFeatures = [
    { name: '월 퀴즈 생성', free: '10개', light: '50개', standard: '무제한' },
    { name: 'AR 오브젝트', free: '5개', light: '20개', standard: '전체' },
    { name: '문제 수/퀴즈', free: '10개', light: '30개', standard: '무제한' },
    { name: 'Excel 업로드', free: false, light: true, standard: true },
    { name: '프리미엄 오브젝트', free: false, light: false, standard: true },
    { name: '팀 협업', free: false, light: false, standard: true },
    { name: '분석 리포트', free: '기본', light: '상세', standard: '고급' },
    { name: '지원', free: '커뮤니티', light: '이메일', standard: '우선' },
  ]

  const formatPrice = (price) => {
    return price.toLocaleString('ko-KR')
  }

  const handleSubscribe = (planName) => {
    // TODO: 결제 로직 구현
    alert(`${planName} 플랜 구독 기능은 준비 중입니다.`)
  }

  return (
    <Container theme={theme}>
      <BackButton theme={theme} onClick={() => navigate(-1)}>
        <ArrowLeft size={18} />
        돌아가기
      </BackButton>

      <Header>
        <Title theme={theme}>요금제 선택</Title>
        <Subtitle theme={theme}>나에게 맞는 플랜을 선택하세요</Subtitle>
      </Header>

      <ToggleContainer>
        <ToggleLabel theme={theme} $active={!isAnnual}>월간</ToggleLabel>
        <ToggleSwitch $isAnnual={isAnnual} onClick={() => setIsAnnual(!isAnnual)} />
        <ToggleLabel theme={theme} $active={isAnnual}>연간</ToggleLabel>
        {isAnnual && <SaveBadge>20% 할인</SaveBadge>}
      </ToggleContainer>

      <PlansContainer>
        {plans.map((plan) => (
          <PlanCard key={plan.name} theme={theme}>
            <PlanName theme={theme}>{plan.name}</PlanName>
            <PlanDescription theme={theme}>{plan.description}</PlanDescription>

            <PriceContainer>
              <PriceRow>
                <Price>
                  <Currency theme={theme}>₩</Currency>
                  <Amount theme={theme}>
                    {formatPrice(isAnnual ? plan.annualPrice : plan.monthlyPrice)}
                  </Amount>
                  <Period theme={theme}>/월</Period>
                </Price>
                {isAnnual && plan.monthlyPrice > 0 && (
                  <OriginalPrice theme={theme}>
                    ₩{formatPrice(plan.monthlyPrice)}/월
                  </OriginalPrice>
                )}
              </PriceRow>
            </PriceContainer>

            <SubscribeButton
              theme={theme}
              onClick={() => handleSubscribe(plan.name)}
            >
              {plan.monthlyPrice === 0 ? '무료로 시작하기' : '구독하기'}
            </SubscribeButton>

            <FeatureList>
              {plan.features.map((feature, index) => (
                <FeatureItem key={index} theme={theme} $included={feature.included}>
                  <FeatureIcon $included={feature.included}>
                    {feature.included ? <Check size={14} /> : <X size={14} />}
                  </FeatureIcon>
                  {feature.text}
                </FeatureItem>
              ))}
            </FeatureList>
          </PlanCard>
        ))}
      </PlansContainer>

      <CompareSection>
        <CompareTitle theme={theme}>플랜 비교</CompareTitle>
        <CompareTable theme={theme}>
          <CompareRow theme={theme}>
            <CompareHeader theme={theme}>기능</CompareHeader>
            <CompareHeader theme={theme} $center>Free</CompareHeader>
            <CompareHeader theme={theme} $center>Light</CompareHeader>
            <CompareHeader theme={theme} $center>Standard</CompareHeader>
          </CompareRow>
          {compareFeatures.map((feature, index) => (
            <CompareRow key={index} theme={theme}>
              <CompareCell theme={theme}>{feature.name}</CompareCell>
              <CompareCell theme={theme} $center>
                {typeof feature.free === 'boolean' ? (
                  feature.free ? <Check size={18} color="#667eea" /> : <X size={18} color="#ccc" />
                ) : feature.free}
              </CompareCell>
              <CompareCell theme={theme} $center>
                {typeof feature.light === 'boolean' ? (
                  feature.light ? <Check size={18} color="#667eea" /> : <X size={18} color="#ccc" />
                ) : feature.light}
              </CompareCell>
              <CompareCell theme={theme} $center>
                {typeof feature.standard === 'boolean' ? (
                  feature.standard ? <Check size={18} color="#667eea" /> : <X size={18} color="#ccc" />
                ) : feature.standard}
              </CompareCell>
            </CompareRow>
          ))}
        </CompareTable>
      </CompareSection>
    </Container>
  )
}
